// =============================================================================
// src/data/atalaia-overlay.ts — Overlay ao vivo: linhas Hasura -> campos da Explorar.
//
// SRP: o UNICO lugar que declara "estes sao os campos servidos ao vivo". Compoe os
//   builders puros de atalaia-mapping.ts e devolve um overlay PARCIAL por municipio
//   (so os campos no escopo: el / idh / eleitoresK / region / micro / th.educacao,
//   renda, vulnerabilidade). NAO inclui habK (vem do IBGE, nao do Hasura) nem camadas
//   novas (PIB/emendas/etc.) — trava de escopo da reforma de runtime 2026-06.
//
// O merge sobre o baseline estatico fica em explore-dataset.applyExploreLiveOverlay.
// =============================================================================

import type {
  AmazonasRegion,
  AtalaiaTerritoryRow,
  ElectoralYear,
  ExploreElectionResult,
  ExploreThematicLevels,
  MunicipalityId,
} from '../app/types';
import {
  ATALAIA_YEARS,
  buildBfCoverageById,
  buildEleitoresKById,
  buildElResultsByYear,
  buildIdebById,
  buildIdhById,
  buildMicroById,
  buildRdpcById,
  buildRegionById,
  bucketizeByQuartile,
} from './atalaia-mapping';

/** Eixos `th` servidos ao vivo (os 3 com fonte no backend). */
type LiveThematicAxis = 'educacao' | 'renda' | 'vulnerabilidade';

/** Overlay parcial de um municipio (so os campos resolvidos ao vivo). */
export interface ExploreMunOverlay {
  readonly el?: Partial<Record<ElectoralYear, ExploreElectionResult>>;
  readonly idh?: number;
  readonly eleitoresK?: number;
  readonly region?: AmazonasRegion;
  readonly micro?: string;
  readonly th?: Partial<Pick<ExploreThematicLevels, LiveThematicAxis>>;
}

/** Versao mutavel usada na construcao (a saida publica e readonly). */
interface MutableOverlay {
  el?: Partial<Record<ElectoralYear, ExploreElectionResult>>;
  idh?: number;
  eleitoresK?: number;
  region?: AmazonasRegion;
  micro?: string;
  th?: { educacao?: number; renda?: number; vulnerabilidade?: number };
}

/** Overlay por id IBGE a partir das linhas gold_territory ao vivo. */
export function buildExploreOverlay(
  rows: readonly AtalaiaTerritoryRow[],
): Record<MunicipalityId, ExploreMunOverlay> {
  const out: Record<MunicipalityId, MutableOverlay> = {};
  applyElOverlay(out, rows);
  applyScalar(out, buildEleitoresKById(rows), 'eleitoresK');
  applyScalar(out, buildIdhById(rows), 'idh');
  applyRegion(out, buildRegionById(rows));
  applyMicro(out, buildMicroById(rows));
  applyThematicAxes(out, rows);
  return out;
}

/** Devolve o overlay de um id, criando-o (vazio) na primeira vez. */
function ensureOverlay(
  out: Record<MunicipalityId, MutableOverlay>,
  id: MunicipalityId,
): MutableOverlay {
  const existing = out[id];
  if (existing) return existing;
  const created: MutableOverlay = {};
  out[id] = created;
  return created;
}

/** Transpoe { ano -> id -> resultado } em overlay.el por municipio. */
function applyElOverlay(
  out: Record<MunicipalityId, MutableOverlay>,
  rows: readonly AtalaiaTerritoryRow[],
): void {
  const byYear = buildElResultsByYear(rows);
  for (const year of ATALAIA_YEARS) {
    for (const [id, result] of Object.entries(byYear[year])) {
      (ensureOverlay(out, id).el ??= {})[year] = result;
    }
  }
}

/** Aplica um campo numerico escalar (idh / eleitoresK) por municipio. */
function applyScalar(
  out: Record<MunicipalityId, MutableOverlay>,
  byId: Record<MunicipalityId, number>,
  key: 'idh' | 'eleitoresK',
): void {
  for (const [id, value] of Object.entries(byId)) ensureOverlay(out, id)[key] = value;
}

/** Aplica a mesorregiao (AmazonasRegion) por municipio. */
function applyRegion(
  out: Record<MunicipalityId, MutableOverlay>,
  byId: Record<MunicipalityId, AmazonasRegion>,
): void {
  for (const [id, region] of Object.entries(byId)) ensureOverlay(out, id).region = region;
}

/** Aplica a microrregiao por municipio. */
function applyMicro(
  out: Record<MunicipalityId, MutableOverlay>,
  byId: Record<MunicipalityId, string>,
): void {
  for (const [id, micro] of Object.entries(byId)) ensureOverlay(out, id).micro = micro;
}

/** Deriva os 3 eixos tematicos reais (quartil de IDEB / RDPC / cobertura BF). */
function applyThematicAxes(
  out: Record<MunicipalityId, MutableOverlay>,
  rows: readonly AtalaiaTerritoryRow[],
): void {
  applyThematicAxis(out, bucketizeByQuartile(buildIdebById(rows)), 'educacao');
  applyThematicAxis(out, bucketizeByQuartile(buildRdpcById(rows)), 'renda');
  applyThematicAxis(out, bucketizeByQuartile(buildBfCoverageById(rows)), 'vulnerabilidade');
}

/** Aplica um eixo tematico (nivel 0..3) por municipio. */
function applyThematicAxis(
  out: Record<MunicipalityId, MutableOverlay>,
  byId: Record<MunicipalityId, number>,
  axis: LiveThematicAxis,
): void {
  for (const [id, level] of Object.entries(byId)) (ensureOverlay(out, id).th ??= {})[axis] = level;
}
